import { t } from 'i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utils
import { parseFontAwesomeClass } from '../../../utils/fontawesome/config';

// Components
import ItemListCard from '../ItemListCard/ItemListCard';
import ItemListCardFooterLinks from '../ItemListCard/ItemListCardFooterLinks';
import { DetailRowTable } from '../DetailRow/DetailRow';
import DashboardItemRenderer from '../DashboardItemRenderer/DashboardItemRenderer';
import Picto, { availablePictos } from '../../atoms/Picto/Picto';

// Styles
import styles from './DashboardCard.module.css';

/**
 * Render a fieldset icon from either a FontAwesome class (API-provided) or a Picto key.
 * @param {string | null} icon
 * @param {string} className
 * @returns {JSX.Element | null}
 */
const renderFieldsetIcon = (icon, className) => {
  const iconDefinition = parseFontAwesomeClass(icon);
  if (iconDefinition) {
    return <FontAwesomeIcon icon={/** @type {any} */ (iconDefinition)} className={className} />;
  }
  if (icon && Object.prototype.hasOwnProperty.call(availablePictos, icon)) {
    const pictoIcon = /** @type {keyof typeof availablePictos} */ (icon);
    return <Picto icon={pictoIcon} className={className} />;
  }
  return null;
};

/**
 * DashboardCard component - Displays a card for a dashboard fieldset
 * Uses ItemListCard as base for styling, similar to TabContentRenderer
 * @param {object} props - Component props
 * @param {ApiDashboardFieldset} props.fieldset - The fieldset to render
 * @returns {JSX.Element}
 */
function DashboardCard({ fieldset }) {
  if (!fieldset) return null;

  return (
    <ItemListCard
      title={fieldset.legend}
      subTitle={fieldset.subLegend || null}
      footer={fieldset.links?.length ? (
        <ItemListCardFooterLinks
          links={fieldset.links.map(({ url, lib, targetBlank }) => ({
            href: url,
            label: lib,
            targetBlank,
          }))}
        />
      ) : null}
      className={styles['dashboard-card']}
      icon={renderFieldsetIcon(fieldset.icon, styles['dashboard-card-icon'])}
    >
      {fieldset.fields.length > 0 ? (
        <DetailRowTable items={fieldset.fields}>
          {(/** @type {ApiDashboardItem} */ item) => (
            <DashboardItemRenderer item={item} />
          )}
        </DetailRowTable>
      ) : (
        <p>{t('dashboard.noFields')}</p>
      )}
    </ItemListCard>
  );
}

export default DashboardCard;
